import type { TelephonyProvider } from '@/hooks/useTelephonyProvider';

/**
 * Extract the call ID from a BPO action, checking RC first then Dialpad then metadata fallbacks.
 */
export const extractCallId = (
  action: Record<string, unknown>,
): string | null => {
  // Check top-level fields first
  if (typeof action.ringcentral_call_id === 'string' && action.ringcentral_call_id) {
    return action.ringcentral_call_id;
  }
  if (typeof action.dialpad_call_id === 'string' && action.dialpad_call_id) {
    return action.dialpad_call_id;
  }

  // Check metadata fallbacks
  const meta =
    action.metadata && typeof action.metadata === 'object'
      ? (action.metadata as Record<string, unknown>)
      : {};

  if (typeof meta.ringcentral_call_id === 'string' && meta.ringcentral_call_id) {
    return meta.ringcentral_call_id;
  }
  if (typeof meta.dialpad_call_id === 'string' && meta.dialpad_call_id) {
    return meta.dialpad_call_id;
  }
  if (typeof meta.call_id === 'string' && meta.call_id) {
    return meta.call_id;
  }

  return null;
};

/**
 * Detect which telephony provider a call ID came from based on action data.
 */
export const detectCallIdProvider = (
  action: Record<string, unknown>,
): TelephonyProvider => {
  if (typeof action.ringcentral_call_id === 'string' && action.ringcentral_call_id) {
    return 'ringcentral';
  }

  const meta =
    action.metadata && typeof action.metadata === 'object'
      ? (action.metadata as Record<string, unknown>)
      : {};

  if (typeof meta.ringcentral_call_id === 'string' && meta.ringcentral_call_id) {
    return 'ringcentral';
  }

  return 'dialpad';
};

/**
 * Extract the user name from a BPO action, checking both RC and Dialpad fields.
 */
export const extractUserName = (
  action: Record<string, unknown>,
): string | null => {
  const meta =
    action.metadata && typeof action.metadata === 'object'
      ? (action.metadata as Record<string, unknown>)
      : {};

  if (typeof meta.ringcentral_user_name === 'string' && meta.ringcentral_user_name) {
    return meta.ringcentral_user_name;
  }
  if (typeof meta.dialpad_user_name === 'string' && meta.dialpad_user_name) {
    return meta.dialpad_user_name;
  }

  return null;
};

/**
 * Find the most recent call_started action's call ID and provider.
 */
export const findLastCallId = (
  actions?: unknown[],
): { callId: string | null; provider: TelephonyProvider; fromNumber: string | null } => {
  if (!Array.isArray(actions)) {
    return { callId: null, provider: 'dialpad', fromNumber: null };
  }

  const entry = [...actions].reverse().find((a) => {
    if (!a || typeof a !== 'object') return false;
    const t = String((a as { type?: unknown }).type || '').toLowerCase();
    return t === 'call_started';
  }) as Record<string, unknown> | undefined;

  if (!entry) {
    return { callId: null, provider: 'dialpad', fromNumber: null };
  }

  const callId = extractCallId(entry);
  const provider = detectCallIdProvider(entry);
  const meta =
    entry.metadata && typeof entry.metadata === 'object'
      ? (entry.metadata as Record<string, unknown>)
      : {};
  const fromNumber =
    typeof meta.from_number === 'string' ? meta.from_number : null;

  return { callId, provider, fromNumber };
};
